import React, { Fragment, useState, useRef, useEffect } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

const WHATSAPP_URL =
  "https://wa.me/971545740174?text=Hello%20GlobalGiftPass%20Concierge%2C%20I%20would%20like%20to%20redeem%20my%20voucher.";

const WHATSAPP_CUSTOM_URL =
  "https://wa.me/971545740174?text=Hello%20GlobalGiftPass%20Concierge%2C%20I%27d%20like%20to%20request%20a%20custom%20experience.%20Could%20your%20team%20help%20me%20plan%20something%20personalized%3F";

const experiences = [
  {
    title: "YACHT EXPERIENCES",
    desc:
      "Sunset cruises, private yachts, fishing trips, birthday celebrations and more.",
    img:
      "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=800&q=70",
    icon: "⛵",
  },
  {
    title: "STAYCATIONS",
    desc:
      "Luxury hotel stays, resort escapes, villa retreats and weekend getaways.",
    img:
      "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=800&q=70",
    icon: "🏨",
  },
  {
    title: "DINING EXPERIENCES",
    desc:
      "Fine dining, restaurant vouchers, group dining and exclusive reservations.",
    img:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=70",
    icon: "🍽️",
  },
  {
    title: "WELLNESS & LIFESTYLE",
    desc:
      "Spa treatments, salon packages, fitness, beauty and lifestyle experiences.",
    img:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=800&q=70",
    icon: "🌿",
  },
];

const features = [
  {
    title: "VIP Reservations",
    desc: "Priority bookings at top venues",
  },
  {
    title: "Dedicated Support",
    desc: "Personal concierge at your service",
  },
  {
    title: "Exclusive Access",
    desc: "Special privileges & experiences",
  },
  {
    title: "Seamless Redemption",
    desc: "Easy voucher redemption",
  },
  {
    title: "Trusted & Verified",
    desc: "Curated partners you can trust",
  },
];

const steps = [
  {
    n: "1",
    title: "REACH OUT",
    desc:
      "Message us on WhatsApp with your voucher code or experience preference.",
  },
  {
    n: "2",
    title: "WE ASSIST YOU",
    desc:
      "Our concierge team will understand your needs and suggest the best options.",
  },
  {
    n: "3",
    title: "CONFIRM & BOOK",
    desc:
      "We handle the reservations and share all details for your seamless experience.",
  },
  {
    n: "4",
    title: "ENJOY VIP TREATMENT",
    desc: "Arrive, relax and enjoy premium service like a VIP.",
  },
];

const whyRedeem = [
  { title: "Premium Experiences", desc: "Curated for you" },
  { title: "Verified Partners", desc: "Quality & trust guaranteed" },
  { title: "VIP Treatment", desc: "Feel special every time" },
  { title: "Flexible Options", desc: "For every occasion" },
];

const stats = [
  { num: "10,000+", label: "Happy Customers" },
  { num: "500+", label: "Premium Partners" },
  { num: "4.9 ★", label: "Customer Rating" },
  { num: "7 Days a Week", label: "Concierge Support" },
];

const CATEGORY_BUTTONS = [
  "Yacht Packages",
  "Dining Experiences",
  "Spa & Wellness",
  "Staycation",
  "Home Care",
  "Luxury Concierge",
  "Other",
];

// Demo voucher database — for UI/UX demonstration only.
const VOUCHER_DB = {
  GGP1234: {
    status: "active",
    package: "Yacht Sunset Cruise",
    expiry: "2026-12-31",
  },
  GGP5678: {
    status: "redeemed",
    package: "Fine Dining Experience",
    redeemedOn: "2026-02-14",
    reference: "BK-20892",
  },
  GGP9999: {
    status: "expired",
    package: "Spa Wellness Package",
    expiry: "2025-08-15",
  },
  GGP0001: {
    status: "pending",
    package: "Luxury Staycation",
  },
};

const initialBotMessage = {
  from: "bot",
  text:
    "Welcome to GlobalGiftPass Concierge 👋\nWe're here to help you redeem and book your premium experiences including yacht cruises, dining, spa, staycations, home care, and lifestyle packages.\n\nHow may we assist you today?",
  buttons: [
    "Redeem My Gift Card",
    "Book My Experience",
    "Track My Booking",
    "Speak to Concierge",
  ],
};

const Concierge = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([initialBotMessage]);
  const [input, setInput] = useState("");
  const [stage, setStage] = useState("welcome");
  const [attempts, setAttempts] = useState(0);
  const [booking, setBooking] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, chatOpen]);

  const pushBot = (text, buttons) => {
    setMessages((prev) => [...prev, { from: "bot", text, buttons }]);
  };

  const pushUser = (text) => {
    setMessages((prev) => [...prev, { from: "user", text }]);
  };

  const resetChat = () => {
    setMessages([initialBotMessage]);
    setStage("welcome");
    setAttempts(0);
    setBooking({});
    setInput("");
  };

  const handleButton = (label) => {
    pushUser(label);

    if (stage === "welcome") {
      if (label === "Redeem My Gift Card" || label === "Book My Experience") {
        setStage("askVoucher");
        setTimeout(
          () =>
            pushBot(
              "Please share one of the following:\n\n• Voucher Code\n• Order Number\n• Registered Email Address"
            ),
          300
        );
      } else if (label === "Track My Booking") {
        setStage("track");
        setTimeout(
          () =>
            pushBot(
              "Please share your booking reference number for tracking, rescheduling or cancellation support."
            ),
          300
        );
      } else if (label === "Speak to Concierge") {
        setStage("handover");
        setTimeout(
          () =>
            pushBot(
              "You are now being connected to a GlobalGiftPass Concierge Specialist. Please tap the button below to chat with us on WhatsApp.",
              ["Open WhatsApp", "Start Over"]
            ),
          300
        );
      }
      return;
    }

    if (stage === "voucherInvalid") {
      if (label === "Retry Voucher Code") {
        setStage("askVoucher");
        pushBot("Please re-enter your voucher code.");
      } else if (label === "Use Order Number Instead") {
        setStage("askVoucher");
        pushBot("Please enter your order number or registered email address.");
      } else if (label === "Speak to Concierge") {
        connectToWhatsapp();
      }
      return;
    }

    if (stage === "voucherRedeemed" || stage === "voucherExpired") {
      if (label === "Speak to Concierge") {
        connectToWhatsapp();
      } else if (label === "Check Another Code") {
        setStage("askVoucher");
        setAttempts(0);
        pushBot("Please enter another voucher code.");
      }
      return;
    }

    if (stage === "category") {
      setBooking((prev) => ({ ...prev, category: label }));
      setStage("collectDate");
      setTimeout(
        () =>
          pushBot(
            `Great choice — ${label}.\n\nTo confirm your reservation, please share your **Preferred Date** (e.g. 15 May 2026).`
          ),
        300
      );
      return;
    }

    if (stage === "slotUnavailable") {
      if (label === "Another Time" || label === "Another Date") {
        setStage("collectDate");
        pushBot("Please share your preferred date and time again.");
      } else if (label === "Speak to Concierge") {
        connectToWhatsapp();
      }
      return;
    }

    if (stage === "handover" || stage === "confirmed" || stage === "locked") {
      if (label === "Open WhatsApp") {
        window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
      } else if (label === "Start Over") {
        resetChat();
      }
      return;
    }
  };

  const connectToWhatsapp = () => {
    setStage("handover");
    pushBot(
      "You are now being connected to a GlobalGiftPass Concierge Specialist. Please tap the button below to chat with us on WhatsApp.",
      ["Open WhatsApp", "Start Over"]
    );
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    pushUser(text);
    setInput("");

    if (stage === "askVoucher") {
      validateVoucher(text);
      return;
    }

    if (stage === "track") {
      setTimeout(
        () =>
          pushBot(
            `Thank you. We're checking the status of booking **${text}**. Our concierge team will update you shortly via WhatsApp.`,
            ["Open WhatsApp", "Start Over"]
          ),
        400
      );
      setStage("handover");
      return;
    }

    if (stage === "collectDate") {
      setBooking((prev) => ({ ...prev, date: text }));
      setStage("collectTime");
      setTimeout(
        () => pushBot("Preferred time? (e.g. 7:00 PM)"),
        300
      );
      return;
    }

    if (stage === "collectTime") {
      setBooking((prev) => ({ ...prev, time: text }));
      setStage("collectGuests");
      setTimeout(() => pushBot("Number of guests?"), 300);
      return;
    }

    if (stage === "collectGuests") {
      setBooking((prev) => ({ ...prev, guests: text }));
      setStage("collectLocation");
      setTimeout(() => pushBot("Preferred area / location in Dubai?"), 300);
      return;
    }

    if (stage === "collectLocation") {
      setBooking((prev) => ({ ...prev, location: text }));
      setStage("collectRequest");
      setTimeout(
        () =>
          pushBot(
            "Any special request? (Type 'none' if not applicable.)"
          ),
        300
      );
      return;
    }

    if (stage === "collectRequest") {
      const finalBooking = { ...booking, request: text };
      setBooking(finalBooking);
      setStage("pending");
      setTimeout(
        () =>
          pushBot(
            "Thank you. We are checking availability with our partner and will confirm shortly..."
          ),
        300
      );

      setTimeout(() => {
        const reference = `GGP-${Math.floor(100000 + Math.random() * 900000)}`;
        const ok = Math.random() > 0.15;
        if (ok) {
          setStage("confirmed");
          pushBot(
            `Your booking is confirmed ✅\n\n• Package: ${
              finalBooking.category || "Selected"
            }\n• Date: ${finalBooking.date}\n• Time: ${
              finalBooking.time
            }\n• Guests: ${finalBooking.guests}\n• Location: ${
              finalBooking.location
            }\n• Reference: ${reference}\n\nWe look forward to serving you.`,
            ["Open WhatsApp", "Start Over"]
          );
        } else {
          setStage("slotUnavailable");
          pushBot(
            "Unfortunately your requested slot is unavailable. Please choose another option.",
            ["Another Time", "Another Date", "Speak to Concierge"]
          );
        }
      }, 1800);
      return;
    }

    // Default fallback when no stage matches text input
    setTimeout(
      () =>
        pushBot(
          "Thanks for your message. Tap a button below or speak to our concierge for assistance.",
          ["Speak to Concierge", "Start Over"]
        ),
      300
    );
  };

  const validateVoucher = (code) => {
    const cleaned = code.replace(/\s+/g, "").toUpperCase();
    const record = VOUCHER_DB[cleaned];

    if (cleaned.includes("@")) {
      setTimeout(
        () =>
          pushBot(
            `Your registered email **${code}** is being verified. Our concierge team will respond shortly.`,
            ["Open WhatsApp", "Start Over"]
          ),
        500
      );
      setStage("handover");
      return;
    }

    if (!record) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= 3) {
        setStage("locked");
        setTimeout(
          () =>
            pushBot(
              "For your security, voucher verification has been temporarily limited. Please contact concierge support.",
              ["Open WhatsApp", "Start Over"]
            ),
          500
        );
        return;
      }

      setStage("voucherInvalid");
      setTimeout(
        () =>
          pushBot(
            "Sorry, we could not verify this voucher code.\n\nPlease check the code and try again. Ensure there are no spaces or typing errors.",
            ["Retry Voucher Code", "Speak to Concierge", "Use Order Number Instead"]
          ),
        500
      );
      return;
    }

    if (record.status === "active") {
      setStage("category");
      setTimeout(
        () =>
          pushBot(
            `Thank you. Your voucher has been successfully verified ✅\n\n• Package Type: ${record.package}\n• Status: Active\n• Expiry: ${record.expiry}\n\nPlease choose your booking category.`,
            CATEGORY_BUTTONS
          ),
        500
      );
      return;
    }

    if (record.status === "redeemed") {
      setStage("voucherRedeemed");
      setTimeout(
        () =>
          pushBot(
            `Thank you. This voucher code has already been redeemed.\n\n• Previous Booking Date: ${record.redeemedOn}\n• Booking Reference: ${record.reference}\n\nIf you believe this is an error, please contact our concierge support team.`,
            ["Speak to Concierge", "Check Another Code"]
          ),
        500
      );
      return;
    }

    if (record.status === "expired") {
      setStage("voucherExpired");
      setTimeout(
        () =>
          pushBot(
            "This voucher has expired and is no longer valid for booking.\n\nIf you need assistance, please contact our concierge team.",
            ["Speak to Concierge"]
          ),
        500
      );
      return;
    }

    if (record.status === "pending") {
      setStage("handover");
      setTimeout(
        () =>
          pushBot(
            "Your voucher is currently under manual verification. Our concierge team will update you shortly.",
            ["Open WhatsApp", "Start Over"]
          ),
        500
      );
      return;
    }
  };

  const openWhatsApp = () => {
    window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
  };

  const openCustomWhatsApp = () => {
    window.open(WHATSAPP_CUSTOM_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <Fragment>
      <Navbar />

      {/* HERO */}
      <section className="cg-hero">
        <div className="cg-hero-overlay" />
        <div className="cg-hero-inner">
          <div className="cg-hero-content">
            <span className="cg-eyebrow">LUXURY CONCIERGE</span>
            <h1 className="cg-title">
              Your VIP Experience,
              <br />
              <span className="cg-title-gold">Our Priority</span>
            </h1>
            <p className="cg-sub">
              Redeem your GlobalGiftPass vouchers for unforgettable experiences
              with Luxury Concierge services, VIP reservations, and dedicated
              support designed just for you.
            </p>
            <div className="cg-hero-buttons">
              <button className="cg-btn-gold" onClick={openWhatsApp}>
                <span className="cg-wa-icon">💬</span>
                REDEEM NOW ON WHATSAPP
              </button>
              <a href="#cg-experiences" className="cg-btn-outline">
                EXPLORE EXPERIENCES
              </a>
            </div>

            <div className="cg-availability">
              <div className="cg-avail-tag">AVAILABLE 7 DAYS A WEEK</div>
              <div className="cg-avail-time">10AM – 1AM</div>
              <div className="cg-avail-sub">CONCIERGE SUPPORT</div>
            </div>

            <div className="cg-feature-row">
              {features.map((f, i) => (
                <div key={i} className="cg-feature-item">
                  <div className="cg-feature-icon">★</div>
                  <div>
                    <div className="cg-feature-title">{f.title}</div>
                    <div className="cg-feature-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section id="cg-experiences" className="cg-section cg-experiences">
        <div className="cg-divider">
          <span className="cg-divider-line" />
          <h2 className="cg-section-title">EXPERIENCES YOU CAN REDEEM</h2>
          <span className="cg-divider-line" />
        </div>

        <div className="cg-exp-grid">
          {experiences.map((exp, i) => (
            <div key={i} className="cg-exp-card">
              <div
                className="cg-exp-image"
                style={{ backgroundImage: `url(${exp.img})` }}
              >
                <div className="cg-exp-icon-wrap">
                  <span className="cg-exp-icon">{exp.icon}</span>
                </div>
              </div>
              <div className="cg-exp-body">
                <h3 className="cg-exp-title">{exp.title}</h3>
                <p className="cg-exp-desc">{exp.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="cg-vip-banner">
          <div className="cg-vip-icon">VIP</div>
          <div className="cg-vip-text">
            Not sure what you need? Tell us your preferences and our concierge
            team will create a personalized experience for you.
          </div>
          <button className="cg-btn-outline cg-vip-btn" onClick={openCustomWhatsApp}>
            REQUEST CUSTOM EXPERIENCE
          </button>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="cg-section cg-howworks">
        <div className="cg-divider">
          <span className="cg-divider-line" />
          <h2 className="cg-section-title">HOW LUXURY CONCIERGE WORKS</h2>
          <span className="cg-divider-line" />
        </div>

        <div className="cg-steps">
          {steps.map((s, i) => (
            <Fragment key={i}>
              <div className="cg-step">
                <div className="cg-step-num">{s.n}</div>
                <h4 className="cg-step-title">{s.title}</h4>
                <p className="cg-step-desc">{s.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="cg-step-arrow">→</div>
              )}
            </Fragment>
          ))}
        </div>
      </section>

      {/* WHY REDEEM */}
      <section className="cg-section cg-why">
        <div className="cg-divider">
          <span className="cg-divider-line" />
          <h2 className="cg-section-title">WHY REDEEM WITH GLOBALGIFTPASS?</h2>
          <span className="cg-divider-line" />
        </div>

        <div className="cg-why-row">
          {whyRedeem.map((w, i) => (
            <div key={i} className="cg-why-item">
              <div className="cg-why-icon">★</div>
              <div className="cg-why-title">{w.title}</div>
              <div className="cg-why-desc">{w.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cg-section cg-cta-section">
        <div className="cg-cta-card">
          <div className="cg-cta-image" />
          <div className="cg-cta-body">
            <h3 className="cg-cta-title">Ready to Redeem Your Voucher?</h3>
            <p className="cg-cta-sub">
              Experience Dubai like never before with our Luxury Concierge.
            </p>
            <button className="cg-btn-gold" onClick={openWhatsApp}>
              <span className="cg-wa-icon">💬</span>
              REDEEM NOW ON WHATSAPP
            </button>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cg-cta-qr"
            aria-label="Scan or click to chat with us on WhatsApp"
          >
            <div className="cg-qr-placeholder">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(
                  WHATSAPP_URL
                )}`}
                alt="WhatsApp QR Code"
                width="110"
                height="110"
              />
            </div>
            <small>SCAN TO CHAT</small>
            <small className="cg-qr-number">+971 54 574 0174</small>
          </a>
        </div>
      </section>

      {/* STATS */}
      <section className="cg-section cg-stats">
        {stats.map((s, i) => (
          <div key={i} className="cg-stat">
            <div className="cg-stat-num">{s.num}</div>
            <div className="cg-stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      {/* CHATBOT FAB */}
      <button
        className={`cg-fab ${chatOpen ? "cg-fab-open" : ""}`}
        onClick={() => setChatOpen(!chatOpen)}
        aria-label="Open Concierge Chat"
      >
        {chatOpen ? "×" : "💬"}
      </button>

      {/* CHATBOT WINDOW */}
      {chatOpen && (
        <div className="cg-chat-window">
          <div className="cg-chat-header">
            <div>
              <div className="cg-chat-title">GlobalGiftPass Concierge</div>
              <div className="cg-chat-sub">Online · Replies instantly</div>
            </div>
            <button
              className="cg-chat-reset"
              onClick={resetChat}
              title="Start over"
            >
              ↺
            </button>
            <button
              className="cg-chat-close"
              onClick={() => setChatOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="cg-chat-body">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`cg-msg ${
                  m.from === "bot" ? "cg-msg-bot" : "cg-msg-user"
                }`}
              >
                <div className="cg-msg-text">{m.text}</div>
                {m.buttons && m.from === "bot" && (
                  <div className="cg-msg-buttons">
                    {m.buttons.map((b, j) => (
                      <button
                        key={j}
                        className="cg-msg-btn"
                        onClick={() => handleButton(b)}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="cg-chat-input">
            <input
              type="text"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button onClick={handleSend} className="cg-chat-send">
              ➤
            </button>
          </div>
        </div>
      )}

      <Footer />
      <Scrollbar />

      <style>{`
        .cg-hero {
          position: relative;
          min-height: 620px;
          background:
            linear-gradient(120deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.35) 100%),
            url("https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=80");
          background-size: cover;
          background-position: center;
          color: #fff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Playfair Display", serif;
        }
        .cg-hero-inner {
          max-width: 1300px;
          margin: 0 auto;
          padding: 70px 32px 60px;
          position: relative;
          z-index: 2;
        }
        .cg-eyebrow {
          color: #d4a857;
          font-size: 13px;
          letter-spacing: 3px;
          font-weight: 600;
        }
        .cg-title {
          font-size: 60px;
          line-height: 1.05;
          margin: 14px 0 18px;
          font-weight: 600;
          font-family: "Playfair Display", "Times New Roman", serif;
        }
        .cg-title-gold {
          color: #d4a857;
          font-style: italic;
          font-weight: 500;
        }
        .cg-sub {
          font-size: 16px;
          line-height: 1.7;
          color: #cfd2d6;
          max-width: 540px;
          margin-bottom: 28px;
        }
        .cg-hero-buttons {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 36px;
        }
        .cg-btn-gold {
          background: linear-gradient(135deg, #d4a857 0%, #b58a3f 100%);
          color: #1a1208;
          border: none;
          padding: 14px 28px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1.5px;
          border-radius: 4px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: transform .2s ease, box-shadow .2s ease;
          font-family: inherit;
        }
        .cg-btn-gold:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(212, 168, 87, 0.35);
        }
        .cg-wa-icon { font-size: 16px; }
        .cg-btn-outline {
          background: transparent;
          color: #fff;
          border: 1.5px solid #d4a857;
          padding: 14px 28px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1.5px;
          border-radius: 4px;
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: background .2s ease, color .2s ease;
          font-family: inherit;
        }
        .cg-btn-outline:hover {
          background: #d4a857;
          color: #1a1208;
        }

        .cg-availability {
          position: absolute;
          right: 32px;
          top: 50%;
          transform: translateY(-30%);
          background: rgba(0,0,0,0.6);
          border: 1px solid rgba(212, 168, 87, 0.35);
          padding: 18px 26px;
          text-align: center;
          border-radius: 6px;
          backdrop-filter: blur(6px);
          display: none;
        }
        @media (min-width: 992px) {
          .cg-availability { display: block; }
        }
        .cg-avail-tag {
          font-size: 10px;
          color: #d4a857;
          letter-spacing: 1.5px;
          margin-bottom: 6px;
        }
        .cg-avail-time {
          font-size: 26px;
          font-weight: 700;
          color: #fff;
        }
        .cg-avail-sub {
          font-size: 11px;
          letter-spacing: 1.5px;
          color: #cfd2d6;
          margin-top: 4px;
        }

        .cg-feature-row {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 22px;
          margin-top: 30px;
          padding-top: 30px;
          border-top: 1px solid rgba(212, 168, 87, 0.18);
          max-width: 1100px;
        }
        .cg-feature-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }
        .cg-feature-icon {
          width: 36px;
          height: 36px;
          border: 1px solid rgba(212, 168, 87, 0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #d4a857;
          font-size: 14px;
          flex-shrink: 0;
        }
        .cg-feature-title {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #fff;
        }
        .cg-feature-desc {
          font-size: 11px;
          color: #b8bcc2;
          line-height: 1.5;
        }

        .cg-section {
          background: #0b0b0c;
          padding: 70px 32px;
          color: #fff;
        }
        .cg-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          margin-bottom: 50px;
        }
        .cg-divider-line {
          width: 50px;
          height: 1px;
          background: #d4a857;
        }
        .cg-section-title {
          font-size: 22px;
          letter-spacing: 4px;
          color: #fff;
          margin: 0;
          font-weight: 500;
          text-align: center;
        }

        .cg-experiences { background: #0b0b0c; }
        .cg-exp-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 22px;
        }
        .cg-exp-card {
          background: #15110a;
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 8px;
          overflow: hidden;
          transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;
          display: flex;
          flex-direction: column;
        }
        .cg-exp-card:hover {
          transform: translateY(-4px);
          border-color: rgba(212, 168, 87, 0.6);
          box-shadow: 0 14px 30px rgba(0,0,0,0.5);
        }
        .cg-exp-image {
          height: 160px;
          background-size: cover;
          background-position: center;
          position: relative;
        }
        .cg-exp-icon-wrap {
          position: absolute;
          left: 50%;
          bottom: -22px;
          transform: translateX(-50%);
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d4a857 0%, #a07a30 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 16px rgba(0,0,0,0.5);
          border: 2px solid #0b0b0c;
        }
        .cg-exp-icon { font-size: 24px; }
        .cg-exp-body {
          padding: 36px 18px 22px;
          text-align: center;
          flex: 1;
        }
        .cg-exp-title {
          font-size: 13px;
          letter-spacing: 1.5px;
          color: #d4a857;
          margin: 0 0 10px;
          font-weight: 700;
        }
        .cg-exp-desc {
          font-size: 12px;
          line-height: 1.6;
          color: #b8bcc2;
          margin: 0;
        }

        .cg-vip-banner {
          max-width: 1300px;
          margin: 50px auto 0;
          background: rgba(212, 168, 87, 0.06);
          border: 1px solid rgba(212, 168, 87, 0.25);
          border-radius: 8px;
          padding: 22px 26px;
          display: flex;
          align-items: center;
          gap: 22px;
          flex-wrap: wrap;
        }
        .cg-vip-icon {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d4a857, #a07a30);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          color: #1a1208;
          letter-spacing: 1px;
          flex-shrink: 0;
        }
        .cg-vip-text { flex: 1; min-width: 220px; font-size: 14px; color: #d8dce2; }
        .cg-vip-btn { padding: 12px 22px; font-size: 12px; }

        .cg-howworks { background: #0b0b0c; }
        .cg-steps {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .cg-step {
          flex: 1;
          min-width: 160px;
          text-align: center;
        }
        .cg-step-num {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #d4a857;
          color: #1a1208;
          font-weight: 800;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 14px;
        }
        .cg-step-title {
          font-size: 14px;
          letter-spacing: 1.5px;
          color: #fff;
          margin: 0 0 8px;
        }
        .cg-step-desc {
          font-size: 12px;
          color: #b8bcc2;
          line-height: 1.6;
          margin: 0;
        }
        .cg-step-arrow {
          color: #d4a857;
          font-size: 20px;
          flex: 0 0 auto;
        }

        .cg-why { background: #0b0b0c; padding-bottom: 30px; }
        .cg-why-row {
          max-width: 1100px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }
        .cg-why-item { text-align: center; padding: 16px 8px; }
        .cg-why-icon {
          width: 40px;
          height: 40px;
          border: 1px solid rgba(212, 168, 87, 0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #d4a857;
          margin: 0 auto 12px;
        }
        .cg-why-title { font-size: 13px; color: #fff; font-weight: 600; margin-bottom: 4px; }
        .cg-why-desc { font-size: 12px; color: #9aa0a8; }

        .cg-cta-section { background: #0b0b0c; padding-top: 30px; }
        .cg-cta-card {
          max-width: 1200px;
          margin: 0 auto;
          background: linear-gradient(120deg, #15110a 0%, #1d160c 100%);
          border: 1px solid rgba(212, 168, 87, 0.25);
          border-radius: 10px;
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1.4fr 0.8fr;
          align-items: center;
          gap: 24px;
        }
        .cg-cta-image {
          height: 100%;
          min-height: 220px;
          background-image:
            linear-gradient(120deg, rgba(0,0,0,0.4), rgba(0,0,0,0.1)),
            url("https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=70");
          background-size: cover;
          background-position: center;
        }
        .cg-cta-body { padding: 30px 14px; }
        .cg-cta-title {
          font-family: "Playfair Display", serif;
          color: #d4a857;
          font-size: 28px;
          font-style: italic;
          margin: 0 0 10px;
          font-weight: 500;
        }
        .cg-cta-sub { color: #cfd2d6; font-size: 14px; margin-bottom: 18px; }
        .cg-cta-qr {
          padding: 24px;
          text-align: center;
          color: #cfd2d6;
          text-decoration: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          transition: transform .25s ease;
        }
        .cg-cta-qr:hover { transform: translateY(-2px); text-decoration: none; }
        .cg-qr-placeholder {
          width: 122px;
          height: 122px;
          background: #fff;
          border-radius: 8px;
          padding: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
          border: 1px solid rgba(212, 168, 87, 0.3);
          transition: border-color .25s ease, box-shadow .25s ease;
        }
        .cg-qr-placeholder img {
          width: 110px;
          height: 110px;
          display: block;
        }
        .cg-cta-qr:hover .cg-qr-placeholder {
          border-color: #d4a857;
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.35);
        }
        .cg-cta-qr small {
          font-size: 11px;
          letter-spacing: 1.5px;
          color: #d4a857;
          display: block;
        }
        .cg-qr-number {
          color: #cfd2d6 !important;
          letter-spacing: 0.5px !important;
          font-weight: 600;
        }

        .cg-stats {
          background: #0b0b0c;
          border-top: 1px solid rgba(212, 168, 87, 0.15);
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          padding: 36px 24px;
          max-width: 1300px;
          margin: 0 auto;
        }
        .cg-stat { text-align: center; padding: 8px; }
        .cg-stat-num { font-size: 24px; color: #d4a857; font-weight: 700; margin-bottom: 4px; }
        .cg-stat-label { font-size: 12px; color: #b8bcc2; letter-spacing: 1px; }

        /* CHAT FAB */
        .cg-fab {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d4a857, #a07a30);
          color: #1a1208;
          border: none;
          font-size: 26px;
          cursor: pointer;
          box-shadow: 0 8px 22px rgba(0,0,0,0.5);
          z-index: 1100;
          transition: transform .2s ease;
        }
        .cg-fab:hover { transform: scale(1.06); }
        .cg-fab-open { background: #1a1208; color: #d4a857; font-size: 32px; }

        .cg-chat-window {
          position: fixed;
          bottom: 96px;
          right: 24px;
          width: 360px;
          max-width: calc(100vw - 32px);
          height: 540px;
          max-height: calc(100vh - 130px);
          background: #15110a;
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 14px;
          z-index: 1099;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 18px 48px rgba(0,0,0,0.6);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .cg-chat-header {
          background: linear-gradient(135deg, #d4a857, #a07a30);
          color: #1a1208;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .cg-chat-title { font-weight: 700; font-size: 14px; }
        .cg-chat-sub { font-size: 11px; opacity: 0.8; }
        .cg-chat-close, .cg-chat-reset {
          background: transparent;
          border: none;
          color: #1a1208;
          font-size: 22px;
          cursor: pointer;
          line-height: 1;
          padding: 4px 8px;
        }
        .cg-chat-reset { font-size: 18px; }
        .cg-chat-body {
          flex: 1;
          padding: 14px;
          overflow-y: auto;
          background: #0b0b0c;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .cg-msg { max-width: 85%; }
        .cg-msg-bot { align-self: flex-start; }
        .cg-msg-user { align-self: flex-end; }
        .cg-msg-text {
          background: #1f1a10;
          color: #e7e5df;
          padding: 10px 14px;
          border-radius: 12px;
          border-top-left-radius: 4px;
          font-size: 13px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .cg-msg-user .cg-msg-text {
          background: #d4a857;
          color: #1a1208;
          border-top-left-radius: 12px;
          border-top-right-radius: 4px;
        }
        .cg-msg-buttons {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 8px;
        }
        .cg-msg-btn {
          background: transparent;
          color: #d4a857;
          border: 1px solid rgba(212, 168, 87, 0.5);
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 12px;
          cursor: pointer;
          text-align: left;
          transition: background .2s ease, color .2s ease;
        }
        .cg-msg-btn:hover {
          background: #d4a857;
          color: #1a1208;
        }
        .cg-chat-input {
          display: flex;
          gap: 8px;
          padding: 10px;
          border-top: 1px solid rgba(212, 168, 87, 0.18);
          background: #15110a;
        }
        .cg-chat-input input {
          flex: 1;
          background: #0b0b0c;
          border: 1px solid rgba(212, 168, 87, 0.25);
          color: #fff;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 13px;
          outline: none;
        }
        .cg-chat-input input:focus { border-color: #d4a857; }
        .cg-chat-send {
          background: #d4a857;
          color: #1a1208;
          border: none;
          border-radius: 8px;
          padding: 0 14px;
          font-size: 14px;
          cursor: pointer;
        }

        /* Tablet */
        @media (max-width: 1100px) {
          .cg-title { font-size: 44px; }
          .cg-feature-row { grid-template-columns: repeat(3, 1fr); }
          .cg-exp-grid { grid-template-columns: repeat(3, 1fr); }
          .cg-why-row { grid-template-columns: repeat(3, 1fr); }
          .cg-cta-card { grid-template-columns: 1fr 1fr; }
          .cg-cta-image { display: none; }
          .cg-stats { grid-template-columns: repeat(2, 1fr); gap: 18px; }
          .cg-availability { display: none; }
        }

        /* Mobile */
        @media (max-width: 720px) {
          .cg-hero { min-height: auto; }
          .cg-hero-inner { padding: 50px 20px 40px; }
          .cg-title { font-size: 32px; }
          .cg-sub { font-size: 14px; }
          .cg-section { padding: 50px 18px; }
          .cg-section-title { font-size: 16px; letter-spacing: 2.5px; }
          .cg-divider-line { width: 30px; }
          .cg-exp-grid { grid-template-columns: 1fr 1fr; gap: 14px; }
          .cg-feature-row { grid-template-columns: 1fr 1fr; gap: 16px; }
          .cg-feature-title { font-size: 12px; }
          .cg-feature-desc { font-size: 11px; }
          .cg-steps { flex-direction: column; gap: 24px; }
          .cg-step-arrow { transform: rotate(90deg); }
          .cg-why-row { grid-template-columns: 1fr 1fr; }
          .cg-cta-card { grid-template-columns: 1fr; }
          .cg-cta-body { padding: 24px; text-align: center; }
          .cg-cta-qr { padding: 20px 24px 28px; }
          .cg-stats { grid-template-columns: 1fr 1fr; }
          .cg-vip-banner { flex-direction: column; text-align: center; }
          .cg-vip-text { min-width: 0; }
          .cg-hero-buttons { flex-direction: column; align-items: stretch; }
          .cg-btn-gold, .cg-btn-outline { justify-content: center; }
          .cg-fab { bottom: 16px; right: 16px; width: 54px; height: 54px; font-size: 22px; }
          .cg-chat-window {
            right: 8px;
            left: 8px;
            bottom: 80px;
            width: auto;
            max-width: none;
            height: 70vh;
          }
        }

        @media (max-width: 420px) {
          .cg-title { font-size: 26px; }
          .cg-exp-grid { grid-template-columns: 1fr; }
          .cg-feature-row { grid-template-columns: 1fr; }
          .cg-why-row { grid-template-columns: 1fr 1fr; }
        }
      `}</style>
    </Fragment>
  );
};

export default Concierge;
